import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import appConfig from './config/appConfig';

const GradientBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50"></div>
    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-cyan-200/30 to-transparent rounded-full blur-3xl"></div>
  </div>
);

const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`}>
    {children}
  </div>
);

// Multi-Select Dropdown Component with higher z-index
const MultiSelectDropdown = ({ label, options, selectedValues, onChange, placeholder = "Select..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const displayText = selectedValues.length === 0
    ? placeholder
    : selectedValues.length === 1
    ? selectedValues[0]
    : `${selectedValues.length} selected`;

  return (
    <div ref={dropdownRef} className="relative z-[60]">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
      >
        <span className={selectedValues.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
          {displayText}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-[200] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl max-h-60 overflow-y-auto">
          {/* Select All Option */}
          <div
            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-200 flex items-center space-x-2 sticky top-0 bg-white z-[201]"
            onClick={handleSelectAll}
          >
            <input
              type="checkbox"
              checked={selectedValues.length === options.length}
              onChange={() => {}}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-gray-700">
              {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
            </span>
          </div>

          {/* Options */}
          {options.map((option) => (
            <div
              key={option}
              className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex items-center space-x-2"
              onClick={() => handleToggle(option)}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => {}}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">{option}</span>
            </div>
          ))}

          {options.length === 0 && (
            <div className="px-4 py-2 text-gray-500 text-sm">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatusPriorityReport = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState([]);
  const [priorityTotals, setPriorityTotals] = useState({});
  const [statusTotals, setStatusTotals] = useState({});
  const [allStatuses, setAllStatuses] = useState([]);
  const [allPriorities, setAllPriorities] = useState([]);
  const [allCriticalities, setAllCriticalities] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter states - now arrays for multiple selections
  const [statusFilters, setStatusFilters] = useState([]);
  const [priorityFilters, setPriorityFilters] = useState([]);
  const [criticalityFilters, setCriticalityFilters] = useState([]);
  const [levelFilters, setLevelFilters] = useState([]);

  // Get authentication values
  const corporate_account = localStorage.getItem('corporate_account');
  const current_project = localStorage.getItem('current_project');
  const login_token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!current_project || current_project === 'null') {
      setError('Please select a project from the Requirements Portal');
      return;
    }
    fetchReportData();
  }, [current_project]);

  const fetchReportData = async (customFilters = null) => {
    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        corporate_account,
        project_id: current_project
      };

      // Use custom filters if provided, otherwise use state filters
      const statusesToUse = customFilters !== null ? customFilters.status : statusFilters;
      const prioritiesToUse = customFilters !== null ? customFilters.priority : priorityFilters;
      const criticalityToUse = customFilters !== null ? customFilters.criticality : criticalityFilters;
      const levelsToUse = customFilters !== null ? customFilters.level : levelFilters;

      // Add filters as arrays
      if (statusesToUse && statusesToUse.length > 0) {
        requestBody.status = statusesToUse;
      }
      if (prioritiesToUse && prioritiesToUse.length > 0) {
        requestBody.priority = prioritiesToUse;
      }
      if (criticalityToUse && criticalityToUse.length > 0) {
        requestBody.criticality = criticalityToUse;
      }
      if (levelsToUse && levelsToUse.length > 0) {
        requestBody.level = levelsToUse;
      }

      const response = await fetch(appConfig.getUrl('/get_requirement_status_priority_report'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setReportData(data.report_data);
        setPriorityTotals(data.priority_totals);
        setStatusTotals(data.status_totals);
        setAllStatuses(data.all_statuses || []);
        setAllPriorities(data.all_priorities || []);
        setAllCriticalities(data.all_criticalities || []);
        setAllLevels(data.all_levels || []);
      } else {
        setError(data.status_description);
      }
    } catch (err) {
      setError('Failed to fetch report data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchReportData();
  };

  const handleRefresh = () => {
    // Refresh with current filters - explicitly call without parameters
    fetchReportData();
  };

  const handleClearFilters = () => {
    // Clear all filter states
    setStatusFilters([]);
    setPriorityFilters([]);
    setCriticalityFilters([]);
    setLevelFilters([]);

    // Immediately fetch data with empty filters
    fetchReportData({
      status: [],
      priority: [],
      criticality: [],
      level: []
    });
  };

  const hasActiveFilters = statusFilters.length > 0 || priorityFilters.length > 0 || criticalityFilters.length > 0 || levelFilters.length > 0;
  const totalActiveFilters = statusFilters.length + priorityFilters.length + criticalityFilters.length + levelFilters.length;

  // Build table data structure using dynamic values
  const buildTableData = () => {
    const tableData = {};

    // Get unique statuses and priorities from report data for table display
    const displayStatuses = [...new Set(reportData.map(item => item.status))].sort();
    const displayPriorities = [...new Set(reportData.map(item => item.priority))].sort((a, b) => {
      const order = { 'High': 1, 'Medium': 2, 'Low': 3, 'Very Low': 4, 'Unassigned': 5 };
      return (order[a] || 99) - (order[b] || 99);
    });

    // Initialize structure
    displayStatuses.forEach(status => {
      tableData[status] = {};
      displayPriorities.forEach(priority => {
        tableData[status][priority] = 0;
      });
    });

    // Fill in data
    reportData.forEach(item => {
      if (tableData[item.status] && displayPriorities.includes(item.priority)) {
        tableData[item.status][item.priority] = item.count;
      }
    });

    return { tableData, displayStatuses, displayPriorities };
  };

  // Build chart data using dynamic values
  const buildChartData = () => {
    const { tableData, displayStatuses, displayPriorities } = buildTableData();
    const chartData = [];

    displayStatuses.forEach(status => {
      const dataPoint = { status };
      displayPriorities.forEach(priority => {
        dataPoint[priority] = tableData[status][priority];
      });
      chartData.push(dataPoint);
    });

    // Add totals
    const totalsPoint = { status: 'Totals' };
    displayPriorities.forEach(priority => {
      totalsPoint[priority] = priorityTotals[priority] || 0;
    });
    chartData.push(totalsPoint);

    return { chartData, displayPriorities };
  };

  const { tableData, displayStatuses, displayPriorities } = buildTableData();
  const { chartData } = buildChartData();

  // Calculate grand total
  const grandTotal = Object.values(priorityTotals).reduce((sum, val) => sum + val, 0);

  // Generate dynamic colors for priorities
  const generatePriorityColors = () => {
    const baseColors = [
      '#3b82f6', // blue
      '#06b6d4', // cyan
      '#0ea5e9', // sky
      '#60a5fa', // light blue
      '#2563eb', // blue-600
      '#0284c7', // sky-600
      '#0891b2', // cyan-600
      '#0369a1', // sky-700
      '#1e40af', // blue-800
      '#164e63', // cyan-900
    ];

    const colors = {};
    displayPriorities.forEach((priority, index) => {
      colors[priority] = baseColors[index % baseColors.length];
    });
    return colors;
  };

  const priorityColors = generatePriorityColors();

  if (!current_project || current_project === 'null') {
    return (
      <div className="min-h-screen">
        <GradientBackground />
        <Header />
        <div className="max-w-7xl mx-auto mt-24 px-4">
          <GlassCard className="p-8 text-center">
            <div className="text-amber-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Project Selected</h2>
            <p className="text-gray-600 mb-6">Please select a project from the Requirements Portal to view reports.</p>
            <button
              onClick={() => navigate('/RequirementsPortal')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium"
            >
              Go to Requirements Portal
            </button>
          </GlassCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <GradientBackground />
      <Header />

      <div className="max-w-7xl mx-auto mt-24 px-4 pb-8">
        {/* Header Section */}
        <div className="mb-8">
          <GlassCard className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Status vs Priority Report</h1>
                  <p className="text-sm text-gray-600">Requirements distribution by status and priority</p>
                </div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Refreshing...</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Refresh</span>
                  </span>
                )}
              </button>
            </div>
          </GlassCard>
        </div>

        {/* Filters Section - Increased z-index and added proper spacing */}
        <div className="mb-16 relative z-50">
          <GlassCard className="p-6 overflow-visible relative z-50">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
              </h2>
              {hasActiveFilters && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {totalActiveFilters} Filter{totalActiveFilters !== 1 ? 's' : ''} Active
                </span>
              )}
            </div>

            {/* Updated grid with proper z-index and spacing */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 relative z-[60]">
              {/* Status Multi-Select Filter */}
              <MultiSelectDropdown
                label="Status"
                options={allStatuses}
                selectedValues={statusFilters}
                onChange={setStatusFilters}
                placeholder="All Statuses"
              />

              {/* Priority Multi-Select Filter */}
              <MultiSelectDropdown
                label="Priority"
                options={allPriorities}
                selectedValues={priorityFilters}
                onChange={setPriorityFilters}
                placeholder="All Priorities"
              />

              {/* Criticality Multi-Select Filter */}
              <MultiSelectDropdown
                label="Criticality"
                options={allCriticalities}
                selectedValues={criticalityFilters}
                onChange={setCriticalityFilters}
                placeholder="All Criticalities"
              />

              {/* Functional Level Multi-Select Filter */}
              <MultiSelectDropdown
                label="Functional Level"
                options={allLevels}
                selectedValues={levelFilters}
                onChange={setLevelFilters}
                placeholder="All Levels"
              />
            </div>

            {/* Filter Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Apply Filters</span>
                </span>
              </button>

              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-lg hover:from-gray-500 hover:to-gray-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Clear All Filters</span>
                  </span>
                </button>
              )}
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Active Filters:</p>
                <div className="flex flex-wrap gap-2">
                  {statusFilters.map(status => (
                    <span key={status} className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Status: {status}</span>
                      <button
                        onClick={() => setStatusFilters(statusFilters.filter(s => s !== status))}
                        className="ml-1 hover:text-blue-900"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {priorityFilters.map(priority => (
                    <span key={priority} className="px-3 py-1 bg-cyan-200 text-cyan-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Priority: {priority}</span>
                      <button
                        onClick={() => setPriorityFilters(priorityFilters.filter(p => p !== priority))}
                        className="ml-1 hover:text-cyan-900"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {criticalityFilters.map(criticality => (
                    <span key={criticality} className="px-3 py-1 bg-sky-200 text-sky-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Criticality: {criticality}</span>
                      <button
                        onClick={() => setCriticalityFilters(criticalityFilters.filter(c => c !== criticality))}
                        className="ml-1 hover:text-sky-900"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {levelFilters.map(level => (
                    <span key={level} className="px-3 py-1 bg-purple-200 text-purple-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Level: {level}</span>
                      <button
                        onClick={() => setLevelFilters(levelFilters.filter(l => l !== level))}
                        className="ml-1 hover:text-purple-900"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>
        </div>

        {error && (
          <div className="mb-8">
            <GlassCard className="p-4 bg-red-50 border-red-200">
              <div className="flex items-center space-x-2 text-red-700">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            </GlassCard>
          </div>
        )}

        {loading ? (
          <GlassCard className="p-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <svg className="animate-spin h-12 w-12 text-blue-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-600">Loading report data...</p>
            </div>
          </GlassCard>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Table Section */}
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Data Table</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border-2 border-blue-300 shadow-lg rounded-lg overflow-hidden">
                  <thead>
                    {/* Main Header Row */}
                    <tr>
                      <th className="border-2 border-white bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 text-left text-white font-bold text-lg">
                        Status
                      </th>
                      <th
                        colSpan={displayPriorities.length + 1}
                        className="border-2 border-white bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 text-center text-white font-bold text-lg"
                      >
                        Priority
                      </th>
                    </tr>
                    {/* Priority Columns Header Row */}
                    <tr className="bg-blue-100">
                      <th className="border-2 border-white bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4 text-left text-white font-bold text-lg">
                        {/* Empty cell for status column */}
                      </th>
                      {displayPriorities.map(priority => (
                        <th key={priority} className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 px-6 py-4 text-center text-blue-700 font-bold text-lg">
                          {priority}
                        </th>
                      ))}
                      <th className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 px-6 py-4 text-center text-blue-700 font-bold text-lg">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayStatuses.map((status, idx) => (
                      <tr key={status} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                        <td className="border-2 border-blue-100 px-6 py-4 font-bold text-gray-800 text-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                          {status}
                        </td>
                        {displayPriorities.map(priority => (
                          <td key={priority} className="border-2 border-blue-100 px-6 py-4 text-center text-gray-700 text-lg">
                            {tableData[status][priority]}
                          </td>
                        ))}
                        <td className="border-2 border-blue-100 px-6 py-4 text-center font-semibold text-blue-600 text-lg">
                          {statusTotals[status] || 0}
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-gradient-to-r from-blue-100 to-cyan-100 hover:from-blue-150 hover:to-cyan-150 transition-colors">
                      <td className="border-2 border-white px-6 py-4 font-bold text-blue-800 text-lg bg-gradient-to-r from-blue-200 to-cyan-200">
                        Totals
                      </td>
                      {displayPriorities.map(priority => (
                        <td key={priority} className="border-2 border-white px-6 py-4 text-center font-bold text-blue-700 text-lg">
                          {priorityTotals[priority] || 0}
                        </td>
                      ))}
                      <td className="border-2 border-white px-6 py-4 text-center font-bold text-blue-800 text-lg">
                        {grandTotal}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </GlassCard>

            {/* Chart Section */}
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Visual Chart</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {displayPriorities.map(priority => (
                    <Bar
                      key={priority}
                      dataKey={priority}
                      fill={priorityColors[priority]}
                      name={`Priority: ${priority}`}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </GlassCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusPriorityReport;